<template>
  <v-card class="white elevation-1">
    <v-card-title
      class="cursor-pointer grey lighten-4 primary--text mb-7"
      @click="showCard = !showCard"
    >
      <v-icon class="ml-2">mdi-paperclip</v-icon>
      اضافة مرفقات
    </v-card-title>

    <v-card-text class="pa-6 mb-8" v-show="showCard">
      <!-- ATTACHMENTS TYPE -->
      <div :key="typeName" v-for="(attachmentType, typeName) in attachmentTypes" class="mb-5 pa-3 elevation-1">
        <!-- ATTACHMENT TYPE LABEL -->
        <h4
          class="secondary--text text--lighten-2 mb-4 grey lighten-5 pa-3"
        >إرفاق {{ attachmentType.label }}</h4>

        <!-- ATTACHMENT -->
        <div :key="index" v-for="(attachment, index) in attachmentType.list">
          <v-row>
            <v-col cols="12" md="5">
              <!-- ATTACHMENT NAME -->
              <v-text-field
                dense
                outlined
                :disabled="!!attachment.file"
                :label="`اسم الملف رقم ${index + 1}`"
                :hint="attachmentType.canUpload ? 'يرجى كتابة اسم الملف قبل اختياره' : null"
                persistent-hint
                v-model="attachmentTypes[typeName].list[index].name"
              />
            </v-col>

            <v-col cols="12" md="7">
              <!-- HIDDEN FILE INPUT -->
              <v-file-input
                v-show="false"
                v-if="attachmentType.canUpload"
                :ref="`upload_file_${typeName}_${index}`"
                @change="uploadFile(typeName, index)"
                v-model="attachmentTypes[typeName].list[index].file"
              ></v-file-input>

              <v-text-field
                dense
                outlined
                :loading="attachment.loading"
                :label="`رابط الملف رقم ${index + 1}`"
                v-model="attachmentTypes[typeName].list[index].path"
                :hint="typeName == 'pdf' ? 'في حالة ارفاق ملف من خارج الموقع. قد يقوم زر تحميل المرفق بعرضه بدلا من تحميله' : ''"
                :error-messages="attachmentTypes[typeName].list[index].errors.file || attachmentTypes[typeName].list[index].errors.path"
              >
                <template v-if="attachmentType.canUpload" v-slot:prepend>
                  <!-- UPLOAD ATTACHMENT -->
                  <v-btn
                    title="رفع ملف"
                    text
                    small
                    icon
                    color="info"
                    @click="$refs[`upload_file_${typeName}_${index}`][0].$refs.input.click()"
                  >
                    <v-icon>mdi-upload</v-icon>
                  </v-btn>
                </template>

                <!-- DELETE ATTACHMENT -->
                <template v-slot:append>
                  <v-btn
                    text
                    icon
                    small
                    color="error"
                    @click="removeFile(typeName, index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </div>

        <!-- APPEND ATTACHMENT -->
        <v-btn
          icon
          large
          color="grey"
          class="d-block mx-auto"
          @click="attachmentTypes[typeName].list.push({
            id: null,
            path: null,
            name: null,
            loading: false,
            file: null,
            errors: [],
          })"
        >
          <v-icon>mdi-plus-circle</v-icon>
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>


<script>
/**
 * ### you can use this component in this way:
 * let attachmentsIds = this.$refs.attachmentsUploader.submit();
 * 
 * ### usage:
 * - any attachment file will be submitted instantly to the server
 *   and the server response will have the attachment { id, path }
 * 
 * - when we call the submit method we will submit any attachment that
 *   doesn't have an id.
 * 
 * - finally we return array of attachments ids
 */
export default {
  name: 'attachments-uploader',

  data: function () {
    return {
      attachmentTypes: {
        soundcloud: {
          label: 'صوتيات ساوندكلاود',
          list: []
        },
        video: {
          label: 'فيديوهات يوتيوب',
          list: []
        },
        image: {
          label: 'صور',
          list: [],
          canUpload: true
        },
        pdf: {
          label: 'ملفات pdf',
          list: [],
          canUpload: true
        },
        other: {
          label: 'ملفات أخرى',
          list: [],
          canUpload: true
        }
      },

      showCard: false
    }
  },


  methods: {
    /**
     * upload the file of the attachment: typeName > list > attachmentIndex
     * - each attachment object contains { id, file, path, loading, errors }
     * 
     * @param string   typeName
     * @param integer attachmentIndex
     */
    uploadFile(typeName, attachmentIndex) {
      const attachment = this.attachmentTypes[typeName].list[attachmentIndex];
      attachment.loading = true;

      // build the form payload
      let payload = new FormData;
      payload.append('type', typeName);
      payload.append('file', attachment.file);
      if (attachment.name) payload.append('name', attachment.name);

      // upload the attachment to the server
      return this.axios.post('/admin/attachments/upload', payload)
        .then((response) => {
          attachment.id = response.data.attachment.id;
          attachment.path = typeName == 'pdf' 
            ? response.data.attachment.path.view
            : response.data.attachment.path;
        })
        .catch((error) => {
          if (error.response.status === 422) {
            attachment.errors = error.response.data.errors;
          }

          this.$bus.$emit("showSnackbar", {
            text: `تعذر رفع مرفق من نوع ${typeName}. رقم ${attachmentIndex + 1}`,
            color: "accent",
          });
        })
        .finally(() => {
          attachment.loading = false;
        });
    },

    /**
     * remove the attachment: typeName > list > attachmentIndex
     * from the server and from the list
     */
    async removeFile(typeName, attachmentIndex) {
      const attachment = this.attachmentTypes[typeName].list[attachmentIndex];
      attachment.loading = true;

      // remove attachment from server
      if (attachment.id) {
        try {
          await this.axios.delete(`/admin/attachments/${attachment.id}`);
        } catch(e) {}
      }

      // remove the attachment from the attachments list
      this.attachmentTypes[typeName].list.splice(attachmentIndex, 1);
      attachment.loading = false;
    },

    /**
     * send the attachments to the server
     * 
     * @return array of attachments ids
     */
    async submit() {
      let attachmentsIds = [];

      // soundcloud, video ...
      for (const attachmentTypeName in this.attachmentTypes) {
        const attachmentType = this.attachmentTypes[attachmentTypeName];

        // attachments objects { id, name, path, file, loading } ...
        for (let index = 0; index < attachmentType.list.length; index++) {
          const attachment = attachmentType.list[index];

          attachment.loading = true;

          // attachment already submitted to server
          if (attachment.id) {
            attachmentsIds.push(attachment.id);
            attachment.loading = false;
            continue;
          }

          // send the attachment to the server
          let payload = { type: attachmentTypeName };
          if (attachment.name) payload.name = attachment.name;
          if (attachment.path) payload.path = attachment.path;

          await this.axios.post('/admin/attachments/upload', payload)
            .then((response) => {
              attachmentsIds.push(response.data.attachment.id);
            })
            .catch((error) => {
              if (error.response.status === 422) {
                attachment.errors = error.response.data.errors;
              }

              this.$bus.$emit("showSnackbar", {
                text: `تعذر رفع مرفق من نوع ${attachmentType.label}. رقم ${index + 1}`,
                color: "accent",
              });

              throw new Error;
            })
            .finally(() => {
              attachment.loading = false;
            });
        }
      }

      return attachmentsIds;
    }
  }
}
</script>